import { useAnalytics } from '@ecosystem/newron-client-lib';
import { containers, elements, feedback } from '@ecosystem/newron-design-system';
import React, { Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import PerformanceAnalysisLineChart from '@data-visualization/performance-analysis-line-chart/PerformanceAnalysisLineChart';
import { ExportPerformanceBySegmentResp } from '@data-visualization/performance-analysis-line-chart/Types';
import { RetailerKpiOption, SaleType } from '@models/Products';
import {
  PERFORMANCE_ANALYSIS_CARD_TITLE,
  PERFORMANCE_ANALYSIS_RETAILER_CARD_TITLE,
  PERFORMANCE_ANALYSIS_TREND_RETAILER_TOOLTIP_MESSAGE,
} from '@pages/Utilities';
import {
  ChartElementName,
  ChartElementType,
  chartInteractionTrackingFunction,
  ChartInteractionTrackingFunctionParam,
  CHART_RADIO_BUTTON_TRACKING_TITLE,
} from '@utils/page-analytics/model/ChartInteractionTracking';
import { getPageTypeWithPageName, ShareType } from '@utils/pageConfig/Types';
import { capitalizeFirstLetter } from '@utils/StringUtils';
import { ProductsRetailerButtonGroup } from '../products-line-charts/products-retailer-button-group/ProductsRetailerButtonGroup';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';

export interface PerformanceAnalysisRetailerCardProps {
  data: ExportPerformanceBySegmentResp;
  selectedPeriodicity: Periodicity;
  currencySymbol: string;
  selectedLineChartSaleType: SaleType;
  selectedRetailerOption: RetailerKpiOption;
  setSelectedRetailerOption: Dispatch<SetStateAction<RetailerKpiOption>>;
  onChangeSelectedRetailerOption: any;
  noResultHandler: () => void;
  noPeriodExistForMulticountry?: boolean;
  excludeFilter?: boolean;
  isExplorePage?: boolean;
}

const PerformanceAnalysisRetailerCard: React.FC<PerformanceAnalysisRetailerCardProps> = ({
  data,
  selectedPeriodicity,
  currencySymbol,
  selectedLineChartSaleType,
  selectedRetailerOption,
  setSelectedRetailerOption,
  onChangeSelectedRetailerOption,
  noResultHandler,
  noPeriodExistForMulticountry,
  excludeFilter,
  isExplorePage,
}) => {
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);

  const handleChartInteractionTracking = (shareMix: string) => {
    const trackingParam: ChartInteractionTrackingFunctionParam = {
      eventTitle: CHART_RADIO_BUTTON_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      elementName: ChartElementName.SHOW,
      elementType: ChartElementType.FILTER,
      optionChosen: capitalizeFirstLetter(shareMix),
      chartName: `${PERFORMANCE_ANALYSIS_CARD_TITLE} Retailer Trend`,
      access: pagesConfig.access,
    };
    chartInteractionTrackingFunction(trackingParam);
  };

  const handleClick = (value: string) => {
    onChangeSelectedRetailerOption(value as ShareType);
    handleChartInteractionTracking(value);
  };
  const { Card } = containers;
  const { Tooltip } = feedback;
  return (
    <Card>
      <Card.Title title={PERFORMANCE_ANALYSIS_RETAILER_CARD_TITLE}>
        <div className="flex pl-4">
          <Tooltip testId="performance-analysis-retailer-card-tooltip" placement="top" theme="light">
            <Tooltip.Content>
              <p>{PERFORMANCE_ANALYSIS_TREND_RETAILER_TOOLTIP_MESSAGE}</p>
            </Tooltip.Content>
            <div>
              <elements.Icon color="brand">
                <elements.Icon.icons.HelpOutline />
              </elements.Icon>
            </div>
          </Tooltip>
        </div>
        <Card.Title.Actions>
          {!isExplorePage && (
            <ProductsRetailerButtonGroup
              selectedRetailerOption={selectedRetailerOption}
              setSelectedOption={setSelectedRetailerOption}
              disabled={selectedLineChartSaleType === SaleType.price}
              clickHandler={handleClick}
              retailerClientStateKey={'retailerClient'}
              excludeFilter={excludeFilter}
            />
          )}
        </Card.Title.Actions>
      </Card.Title>
      <PerformanceAnalysisLineChart
        data={data}
        selectedPeriodicity={selectedPeriodicity}
        currencySymbol={currencySymbol}
        selectedLineChartSaleType={selectedLineChartSaleType}
        isMarket={false}
        noResultHandler={noResultHandler}
        selectedRetailerOption={selectedRetailerOption}
        noPeriodExistForMulticountry={noPeriodExistForMulticountry}
      />
    </Card>
  );
};

export default PerformanceAnalysisRetailerCard;
